
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Clock, Trophy, Users, Calendar, Target, Activity } from 'lucide-react';
import PermissionGate from '@/components/rbac/PermissionGate';

const SupportDashboard: React.FC = () => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <PermissionGate permission="update_scores">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4 flex items-center space-x-4">
              <Trophy className="h-8 w-8 text-sports-green" />
              <div>
                <h4 className="font-semibold text-white">Games Managed</h4>
                <p className="text-sm text-gray-300">24 this week</p>
              </div>
            </CardContent>
          </Card>
        </PermissionGate>

        <PermissionGate permission="manage_games">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4 flex items-center space-x-4">
              <Clock className="h-8 w-8 text-sports-green" />
              <div>
                <h4 className="font-semibold text-white">Active Games</h4>
                <p className="text-sm text-gray-300">3 live now</p>
              </div>
            </CardContent>
          </Card>
        </PermissionGate>

        <PermissionGate permission="view_schedules">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4 flex items-center space-x-4">
              <Calendar className="h-8 w-8 text-sports-green" />
              <div>
                <h4 className="font-semibold text-white">Upcoming Duties</h4>
                <p className="text-sm text-gray-300">7 assignments</p>
              </div>
            </CardContent>
          </Card>
        </PermissionGate>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PermissionGate permission="update_scores">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6">
              <h4 className="font-semibold mb-4 text-white flex items-center gap-2">
                <Activity className="h-5 w-5 text-sports-green" />
                Live Score Updates
              </h4>
              <div className="space-y-3">
                <div className="p-4 bg-gray-700 rounded-lg border-l-4 border-sports-green">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-medium">Eagles vs Hawks</span>
                    <span className="px-2 py-1 bg-red-500 text-white text-xs rounded">LIVE</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Quarter 3 - 12:45</span>
                    <span className="text-sports-green font-bold">42-38</span>
                  </div>
                </div>
                <div className="p-4 bg-gray-700 rounded-lg border-l-4 border-yellow-400">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-medium">Storm vs Thunder</span>
                    <span className="px-2 py-1 bg-yellow-500 text-black text-xs rounded">HALF TIME</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Half Time Break</span>
                    <span className="text-sports-green font-bold">21-19</span>
                  </div>
                </div>
                <div className="p-4 bg-gray-700 rounded-lg border-l-4 border-blue-400">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-medium">Riders vs United</span>
                    <span className="px-2 py-1 bg-blue-500 text-white text-xs rounded">STARTING</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Warm-up in progress</span>
                    <span className="text-gray-400">0-0</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </PermissionGate>

        <PermissionGate permission="manage_games">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6">
              <h4 className="font-semibold mb-4 text-white flex items-center gap-2">
                <Target className="h-5 w-5 text-sports-green" />
                Game Statistics
              </h4>
              <div className="space-y-4">
                <div className="p-4 bg-gray-700 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-medium">Average Game Duration</span>
                    <span className="text-sports-green font-bold">2h 15m</span>
                  </div>
                  <div className="text-sm text-gray-300">Across all games this season</div>
                </div>
                <div className="p-4 bg-gray-700 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-medium">Score Updates</span>
                    <span className="text-sports-green font-bold">1,247</span>
                  </div>
                  <div className="text-sm text-gray-300">Total updates this month</div>
                </div>
                <div className="p-4 bg-gray-700 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-medium">Accuracy Rate</span>
                    <span className="text-sports-green font-bold">99.2%</span>
                  </div>
                  <div className="text-sm text-gray-300">Score keeping accuracy</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </PermissionGate>
      </div>

      <PermissionGate permission="view_schedules">
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <h4 className="font-semibold mb-4 text-white flex items-center gap-2">
              <Users className="h-5 w-5 text-sports-green" />
              Today's Assignments
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="p-3 bg-gray-700 rounded-md border-l-4 border-sports-green">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-white">10:00 AM</span>
                    <span className="text-sports-green text-sm">Field A</span>
                  </div>
                  <p className="text-sm text-gray-300">Youth League - Eagles vs Hawks</p>
                </div>
                <div className="p-3 bg-gray-700 rounded-md border-l-4 border-yellow-400">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-white">2:30 PM</span>
                    <span className="text-yellow-400 text-sm">Court 1</span>
                  </div>
                  <p className="text-sm text-gray-300">Basketball Championship Final</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="p-3 bg-gray-700 rounded-md border-l-4 border-blue-400">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-white">5:00 PM</span>
                    <span className="text-blue-400 text-sm">Field B</span>
                  </div>
                  <p className="text-sm text-gray-300">Adult League - Storm vs United</p>
                </div>
                <div className="p-3 bg-gray-700 rounded-md border-l-4 border-purple-400">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-white">7:30 PM</span>
                    <span className="text-purple-400 text-sm">Court 2</span>
                  </div>
                  <p className="text-sm text-gray-300">Volleyball Tournament</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </PermissionGate>
    </div>
  );
};

export default SupportDashboard;
